import { useDebounceFn } from '@vueuse/core'
import type {
  BasketEPrescriptionModel,
  BasketPrescriptionModel,
  BasketProductModel,
  ProductModelV2,
} from '@develop/ia-api-library'
import { centsToEuro } from '@develop/ia-utils'
import type { RouteRecordNormalized } from '#vue-router'
import type { PharmacyExtended } from '@/models/pharmacy'
import type { FeatureVendor } from '~/models/gdpr-publisher-selection.js'
import type { PharmacyDetails } from '~/models/pharmacy-details.js'

export const useGoogleAnalytics = () => {
  const GA4_TRACKING_ID = 'G-DG7QVVWRXT'

  const pharmacyStore = useCurrentPharmacyStore()
  const userCentrics = useUserCentrics()
  const { user } = useUserStore()

  const isLoaded = () => document.getElementById('GA4Script') != null
  const isAllowedToLoad = computed(() => userCentrics.selectedPublishers.value.find((p: FeatureVendor) => p.name === 'Google Analytics'))
  const pharmacy = computed(() => pharmacyStore.pharmacy)

  const lastScrollPercentage = ref<number>(0)

  const router = useRouter()

  const findRoutes = (routeName: string): RouteRecordNormalized[] => router.getRoutes().filter(route => route.name?.toString().startsWith(routeName))

  const userAreaConsentGroups = findRoutes('kundenkonto-').map((route: RouteRecordNormalized) => ({ name: 'Kundenkonto', routeName: route.name }))
  const checkoutConsentGroups = findRoutes('warenkorb-').map((route: RouteRecordNormalized) => ({ name: 'Warenkorb', routeName: route.name }))
  const registerConsentGroups = findRoutes('register').map((route: RouteRecordNormalized) => ({ name: 'Registrierung', routeName: route.name }))

  const { getProductIndex, getProductListName } = useProductEntrypointList(undefined)

  type GA4PageViewObject = {
    page_title: String
    page_location: String
    page_referrer: String | null
  }

  // TODO: Expand with new Versorger project groups
  const contentGroups = [
    { name: 'Startseite', routeName: 'index' },
    { name: 'Produkt (Apotheke)', routeName: 'pharmacyName-pharmacyPlz-pharmacyId-produkt-productName-pzn' },
    { name: 'Produkt (Apotheke)', routeName: 'pharmacyId-produkt-productName-pzn' },
    { name: 'Apofinder', routeName: 'apofinder' },
    { name: 'Apotheke finden', routeName: 'apotheke-finden' },
    { name: 'Suche', routeName: 'suche' },
    { name: 'Produkt (direkt)', routeName: 'produkt-productName-pzn' },
    { name: 'HIV-Selbsttest', routeName: 'hiv-selbsttest' },
    { name: 'Kontakt', routeName: 'kontakt' },
    ...userAreaConsentGroups,
    { name: 'Markenübersicht', routeName: 'markenuebersicht' },
    { name: 'Markenübersicht', routeName: 'apotheke-pharmacyId-markenuebersicht-slug' },
    { name: 'Produktübersicht', routeName: 'produkte' },
    { name: 'Produktübersicht', routeName: 'pharmacyName-pharmacyPlz-pharmacyId-produkte' },
    { name: 'Produktübersicht', routeName: 'pharmacyId-produkte' },
    { name: 'Themenübersicht', routeName: 'themenuebersicht' },
    { name: 'Themenübersicht', routeName: 'apotheke-pharmacyId-themenuebersicht-slug' },
    { name: 'Rezept-Upload', routeName: 'pharmacyName-pharmacyPlz-pharmacyId-rezept-upload' },
    { name: 'Rezept-Upload', routeName: 'pharmacyId-rezept-upload' },
    { name: 'Shop', routeName: 'shop' },
    { name: 'Shop', routeName: 'apotheke-pharmacyId-shop' },
    { name: 'Startseite (Apotheke)', routeName: 'apotheke-pharmacyName-pharmacyPlz-pharmacyId' },
    { name: 'Startseite (Apotheke)', routeName: 'apotheke-pharmacyId-short' },
    ...registerConsentGroups,
    { name: 'Login', routeName: 'login' },
    { name: 'Resetting', routeName: 'resetting-request' },
    { name: 'Newsletter', routeName: 'newsletter' },
    ...checkoutConsentGroups,
    { name: 'FAQ', routeName: 'faq' },
    { name: 'Über uns', routeName: 'ueber-uns' },
    { name: 'Impressum', routeName: 'impressum' },
    { name: 'Epaper', routeName: 'epaper-abonnieren' },
    { name: 'Registrierung', routeName: 'epaper-registrierung' },
  ]

  window.afterGivenConsentEvents = window.afterGivenConsentEvents || []

  const triggerEvent = (label: string, value?: object | null) => {
    if (!window?.gtag || !isAllowedToLoad.value) {
      window.afterGivenConsentEvents.push({
        label,
        value,
      })
      return
    }

    window.gtag('event', label, value)
  }

  const triggerAfterGivenConsentEvents = () => {
    if (window.afterGivenConsentEvents.length === 0 || !isAllowedToLoad.value)
      return

    window.afterGivenConsentEvents.forEach((eventObject: any) => {
      triggerEvent(eventObject.label, eventObject.value)
    })
    window.afterGivenConsentEvents = []
  }

  const route = useRoute()

  const getGA4ProductObject = (product: any, quantity: Number | undefined = undefined, index: Number | undefined = undefined, productCarouselListName: string | undefined = undefined) => {
    return {
      item_id: product.pzn,
      item_name: product.productName ?? product.name,
      item_brand: typeof product.manufacturer === 'string'
        ? product.manufacturer
        : product.manufacturer?.name,
      item_category: typeof product.categories === 'string'
        ? product.categories
        : product.categories?.map((category: any) => category.name).join(', '),
      item_product_type: product.prescription ? 'rx' : 'otc',
      item_variant: `${product.productDosage ?? product.dosage ?? ''}${product.type ? ` | ${product.type}` : ''}`,
      price: centsToEuro(product.price! ?? product.mainPrice?.value),
      quantity: quantity ?? 1,
      index: index ?? getProductIndex(product.pzn) ?? 1,
      item_list_name: productCarouselListName ?? getProductListName(product.pzn),
    }
  }

  const getGA4PrescriptionObject = (prescription: BasketPrescriptionModel) => {
    return {
      item_id: '00000000',
      item_name: 'Rezept',
      item_category: 'Rezepte',
      item_variant: prescription.note,
      price: 0.0,
    }
  }

  const getGA4EPrescriptionObject = (ePrescription: BasketEPrescriptionModel) => {
    return {
      item_id: '00000000',
      item_name: 'E-Rezept',
      item_category: 'E-Rezepte',
      item_variant: ePrescription.note,
      price: 0.0,
    }
  }

  const getGA4ProductsObject = (cartItems: any, cartItemsAdditionalData: any = null) => {
    if (cartItems?.products.length + cartItems.prescriptions.length + cartItems.ePrescriptions.length === 0)
      return []

    const results: any[] = []

    cartItems.products.forEach((product: ProductModelV2) => {
      if (cartItemsAdditionalData?.length > 0)
        results.push(getGA4ProductObject(product, cartItemsAdditionalData.find((p: any) => p.pzn === product.pzn)?.amount, 1))
      else
        results.push(getGA4ProductObject(product, cartItems.products.find((p: BasketProductModel) => p.pzn === product.pzn)?.amount, 1))
    })

    cartItems.prescriptions.forEach((prescription: BasketPrescriptionModel) => {
      results.push(getGA4PrescriptionObject(prescription))
    })

    cartItems.ePrescriptions.forEach((ePrescription: BasketEPrescriptionModel) => {
      results.push(getGA4EPrescriptionObject(ePrescription))
    })

    return results
  }

  const formStartEvent = (formName: string) => {
    triggerEvent('form_start', {
      form_id: formName,
      send_to: GA4_TRACKING_ID,
      content_group: getContentGroupName(route.name),
    },
    )
  }

  const formSubmitEvent = (formName: string) => {
    triggerEvent('form_submit', {
      form_id: formName,
      send_to: GA4_TRACKING_ID,
      content_group: getContentGroupName(route.name),
    },
    )
  }

  const formSentEvent = (formName: string) => {
    triggerEvent('form_sent', {
      form_id: formName,
      send_to: GA4_TRACKING_ID,
      content_group: getContentGroupName(route.name),
    })
  }

  const formSubmitForRegisterEvent = (formName: string, isMyLifeChecked: boolean) => {
    triggerEvent('form_submit', {
      form_id: formName,
      opt_epaper: isMyLifeChecked ? 'yes' : 'no',
      send_to: GA4_TRACKING_ID,
      content_group: getContentGroupName(route.name),
    })
  }

  const formSentForRegisterEvent = (formName: string, isMyLifeChecked: boolean, isNewsletterChecked = false) => {
    triggerEvent('form_sent', {
      form_id: formName,
      opt_epaper: isMyLifeChecked ? 'yes' : 'no',
      opt_newsletter: isNewsletterChecked ? 'yes' : 'no',
      send_to: GA4_TRACKING_ID,
      content_group: getContentGroupName(route.name),
    })
  }

  const formSentForWhatsAppRegisterEvent = (formName: string, isWhatsAppChecked: boolean) => {
    triggerEvent('form_sent', {
      form_id: formName,
      opt_whatsapp: isWhatsAppChecked ? 'yes' : 'no',
      send_to: GA4_TRACKING_ID,
      content_group: getContentGroupName(route.name),
    })
  }

  const terminBuchenClickEvent = ({ name, id, postcode }: { name: string; id: string; postcode: string }) => {
    triggerEvent('click', {
      click_item: 'termin-buchen',
      click_params: `${id}~${name}~${postcode}`,
      send_to: GA4_TRACKING_ID,
      content_group: getContentGroupName(route.name),
    })
  }

  const prescriptionUploadButtonClickEvent = (pharmacy: PharmacyDetails) => {
    triggerEvent('click', {
      click_item: 'prescription-upload-button',
      click_params: pharmacy ? `${pharmacy.id}~${pharmacy.additionalInformation.name}~${pharmacy.location.postcode}` : '',
      send_to: GA4_TRACKING_ID,
      content_group: getContentGroupName(route.name),
    })
  }

  const terminBuchenLinkClickEvent = (pharmacy: PharmacyExtended) => {
    triggerEvent('click', {
      click_item: 'termin-buchen-link',
      click_params: `${pharmacy.id}~${pharmacy.name}~${pharmacy.postcode}`,
      send_to: GA4_TRACKING_ID,
      content_group: getContentGroupName(route.name),
    })
  }

  const choosePharmacyButtonClickEvent = (pharmacy: any) => {
    triggerEvent('click', {
      click_item: 'apo-auswaehlen',
      click_params: `${pharmacy.id}~${pharmacy.name}~${pharmacy.postcode}`,
      send_to: GA4_TRACKING_ID,
      content_group: getContentGroupName(route.name),
    })
  }

  const switchNewPharmacyButtonClickEvent = (pharmacy: any) => {
    triggerEvent('click', {
      click_item: 'neue-apo-auswaehlen',
      click_params: `${pharmacy.id}~${pharmacy.additionalInformation.name}~${pharmacy.location.postcode}`,
      send_to: GA4_TRACKING_ID,
      content_group: getContentGroupName(route.name),
    })
  }

  const keepOldPharmacyButtonClickEvent = (pharmacy: any) => {
    triggerEvent('click', {
      click_item: 'apo-beibehalten',
      click_params: `${pharmacy.id}~${pharmacy.additionalInformation.name}~${pharmacy.location.postcode}`,
      send_to: GA4_TRACKING_ID,
      content_group: getContentGroupName(route.name),
    })
  }

  const viewSearchresultsEvent = (searchTerm: string, searchResultsCount: number) => {
    triggerEvent('view_search_results', {
      search_term: searchTerm,
      search_results: searchResultsCount,
      send_to: GA4_TRACKING_ID,
      content_group: getContentGroupName(route.name),
    })
  }

  const getTrackingHashtag = () => {
    if (
      window.location.hash.length > 0
      && window.location.hash.match(/^#(w_|header|indi|produkt_|angebot).*$/g)
    ) {
      const hash = window.location.hash.replace('#', '')
      return hash
    }
    return ''
  }

  const getContentGroupName = (routeName: string) => {
    const contentGroup = contentGroups.find(contentGroup => routeName === contentGroup.routeName)
    return contentGroup?.name ?? ''
  }

  const execCustomDimensions = (pageObject: GA4PageViewObject, bestelltyp = '') => {
    if (!isAllowedToLoad.value || !window.gtag)
      return

    const { lastPageBeforeCheckout } = useGeneralTracking()
    const referrerStore = useReferrerStore()

    const themenshopPages = ['apotheke-pharmacyId-themenuebersicht-slug', 'themenuebersicht-slug']
    const markenshopPages = ['apotheke-pharmacyId-markenuebersicht-slug', 'markenuebersicht-slug']

    const affiliation = [...themenshopPages, ...markenshopPages].includes(route.name) ? (route.params as { slug: string }).slug : ''

    const pharmacyData = {
      apotheken_name: '',
      apotheken_adresse: '',
      apotheken_id: '',
      apotheken_plz: '',
    }

    if (pharmacy.value) {
      pharmacyData.apotheken_name = pharmacy.value.additionalInformation.name
      pharmacyData.apotheken_adresse = pharmacyStore.fullAddress
      pharmacyData.apotheken_id = pharmacy.value.id.toString()
      pharmacyData.apotheken_plz = pharmacy.value.location.postcode
    }

    if (!window.dataLayer.find((event: any) => event[0] === 'config')) {
      window.gtag('config', GA4_TRACKING_ID, {
        send_page_view: false,
      })
    }

    window.gtag('set', 'user_properties', {
      ...pharmacyData,
      affiliation,
      isAnonymous: !user,
      url_fragment: getTrackingHashtag(),
      bestelltyp: bestelltyp ?? '',
      lastPageBeforeCheckout: lastPageBeforeCheckout.value ?? '',
    })

    triggerEvent('page_view', {
      page_title: pageObject.page_title,
      page_location: pageObject.page_location,
      page_referrer: pageObject.page_referrer,
      content_group: getContentGroupName(referrerStore.routeName),
    })
  }

  const calcScrollDepth = (scrollTop: number) => {
    let pageHeight = Math.max(
      document.body.scrollHeight,
      document.body.offsetHeight,
      document.documentElement.clientHeight,
      document.documentElement.scrollHeight,
      document.documentElement.offsetHeight,
    )
    pageHeight -= window.innerHeight
    let percent = (scrollTop / pageHeight) * 100
    switch (true) {
      case percent >= 24 && percent <= 26: {
        percent = 25
        break
      }
      case percent >= 49 && percent <= 51: {
        percent = 50
        break
      }
      case percent >= 74 && percent <= 76: {
        percent = 75
        break
      }
      case percent >= 90 && percent <= 100: {
        percent = 100
        break
      }
    }
    return percent
  }

  const trackScrollDepthEvent = () => {
    const calcScrollDepthFn = useDebounceFn(() => {
      const scrolledFromTop = Math.abs(document.documentElement.scrollTop)
      const currentScrollPercentage = calcScrollDepth(scrolledFromTop)
      const allowedPercentages = [25, 50, 75, 100]

      if (currentScrollPercentage !== lastScrollPercentage.value && allowedPercentages.includes(currentScrollPercentage)) {
        set(lastScrollPercentage, currentScrollPercentage)

        triggerEvent(
          'scroll',
          {
            percent_scrolled: currentScrollPercentage,
            send_to: GA4_TRACKING_ID,
            content_group: getContentGroupName(route.name),
          },
        )
      }
    }, 300)

    window.addEventListener('scroll', calcScrollDepthFn)
  }

  const productsViewItemListEvent = (products: ProductModelV2[], productCarouselListName: string | undefined) => {
    const results: any[] = []
    products.forEach((product: ProductModelV2, index: number) => {
      results.push(getGA4ProductObject(product, 1, ++index, productCarouselListName))
    })
    triggerEvent('view_item_list', {
      items: results,
      send_to: GA4_TRACKING_ID,
      content_group: getContentGroupName(route.name),
    })
  }

  const productClickEvent = (product: ProductModelV2, index: number, productCarouselListName: string | undefined) => {
    triggerEvent('select_item', {
      items: [getGA4ProductObject(product, 1, index, productCarouselListName)],
      send_to: GA4_TRACKING_ID,
      content_group: getContentGroupName(route.name),
    })
  }

  const initGoogleConsentMode = () => {
    const { selectedPublishers } = useUserCentrics()
    const isGoogleAdsAllowed = selectedPublishers.value.find(p => p.name === 'Google Ads')
    const isGoogleAnalyticsAllowed = selectedPublishers.value.find(p => p.name === 'Google Analytics')
    setGoogleAdsConsent({
      adStorage: !!isGoogleAdsAllowed,
      userData: !!isGoogleAdsAllowed,
      personalization: !!isGoogleAdsAllowed,
      analyticsStorage: !!isGoogleAnalyticsAllowed,
    })
  }

  const init = () => {
    if (isAllowedToLoad.value) {
      setGoogleAdsConsent()
      if (!isLoaded())
      { return import('./ga.js').then(({ initGa, gaStartEvent }) => initGa(GA4_TRACKING_ID).then(() => {
        initGoogleConsentMode()
        gaStartEvent()
        trackScrollDepthEvent()
      })) }
      else
      { return initGa(GA4_TRACKING_ID).then(() => {
        initGoogleConsentMode()
        gaStartEvent()
        trackScrollDepthEvent()
      }) }
    }
  }

  const pageViewEvent = (pageObject: GA4PageViewObject) => {
    execCustomDimensions(pageObject)
    triggerAfterGivenConsentEvents()
  }

  const promotionViewEvent = (promotions: any[]) => {
    triggerEvent('view_promotion', {
      items: promotions,
      promotion_name: promotions[0]?.promotion_name,
      send_to: GA4_TRACKING_ID,
      content_group: getContentGroupName(route.name),
    })
  }

  const promotionClickEvent = (index: number, promotionName: string, creativeName: string | null = null) => {
    const promotionObject = {
      item_id: '00001111',
      item_name: 'Promo',
      promotion_name: promotionName,
      creative_slot: index,
    }
    if (creativeName)
      promotionObject.creative_name = creativeName

    triggerEvent('select_promotion', {
      items: [promotionObject],
      promotion_name: promotionName,
      send_to: GA4_TRACKING_ID,
      content_group: getContentGroupName(route.name),
    })
  }

  const setGoogleAdsConsent = (consents: any = null) => {
    if ('gtag' in window === false) {
      window.dataLayer = []
      window.gtag = function () {
      // eslint-disable-next-line prefer-rest-params
        window.dataLayer.push(arguments)
      }
    }
    if (!consents) {
      window.dataLayer.push(function () {
        this.reset()
      })
    }
    const hasAnyGoogleConsentModeEvents = window.dataLayer.filter((event: any) => event[0] === 'consent')
    if (hasAnyGoogleConsentModeEvents.length < 1 || consents) {
      window.gtag('consent', consents ? 'update' : 'default', {
        ad_storage: consents?.adStorage ? 'granted' : 'denied',
        ad_user_data: consents?.userData ? 'granted' : 'denied',
        ad_personalization: consents?.personalization ? 'granted' : 'denied',
        analytics_storage: consents?.analyticsStorage ? 'granted' : 'denied',
      })
    }
  }

  return {
    GA4_TRACKING_ID,
    isAllowedToLoad,
    init,
    getGA4ProductObject,
    getGA4ProductsObject,
    getContentGroupName,
    pageViewEvent,
    triggerEvent,
    triggerAfterGivenConsentEvents,
    formStartEvent,
    formSubmitEvent,
    formSentEvent,
    formSubmitForRegisterEvent,
    formSentForRegisterEvent,
    formSentForWhatsAppRegisterEvent,
    terminBuchenClickEvent,
    terminBuchenLinkClickEvent,
    prescriptionUploadButtonClickEvent,
    productsViewItemListEvent,
    productClickEvent,
    keepOldPharmacyButtonClickEvent,
    switchNewPharmacyButtonClickEvent,
    choosePharmacyButtonClickEvent,
    initGoogleConsentMode,
    setGoogleAdsConsent,
    viewSearchresultsEvent,
    promotionViewEvent,
    promotionClickEvent,
  }
}
